import { useSnackbar } from 'notistack';
import { FillHeight } from '@emfood/shared';
import { useQueryClient } from 'react-query';
import { useMemo, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import Iconify from '@emfood/shared/src/components/iconify';

import { DataGrid, GridColDef, GridSortModel, GridPaginationModel } from '@mui/x-data-grid';
import {
  Box,
  Chip,
  Card,
  Grid,
  Stack,
  Button,
  Tooltip,
  Divider,
  useTheme,
  IconButton,
  Typography,
  Pagination,
  CardContent,
  useMediaQuery,
} from '@mui/material';

import { paths } from 'src/routes/paths';

import { useGetCart, useCancelOrder, useAddMultipleToCart } from 'src/api/hooks/api-hooks';
import {
  LogisticStatus,
  BuyerOrderModel,
  LogisticStatusLabels,
  BuyerOrderProductModel,
} from 'src/api/models/orders';

import { usePopover } from 'src/components/custom-popover';
import { LoadingBarHorizontal } from 'src/components/loading';
import { CopyToClipboard } from 'src/components/copy-to-clipboard/view';

interface Props {
  orders?: BuyerOrderModel[];
  ordersTotalCount?: number;
  ordersTotalPages: number;
  isLoading?: boolean;
  paginationModel: GridPaginationModel;
  sortModel: GridSortModel;
  // filterModel: GridFilterModel;
  // onFilterChanged: (model: GridFilterModel) => void;
  onSortChanged: (model: GridSortModel) => void;
  onPaginationModelChanged: (model: GridPaginationModel) => void;
}
// TODO: Color code the status
const getColor = (logisticStatus: LogisticStatus) => {
  switch (logisticStatus) {
    case LogisticStatus.OrderReceived:
      return 'info';
    case LogisticStatus.Edited:
      return 'info';
    case LogisticStatus.Picking:
    case LogisticStatus.Packed:
    case LogisticStatus.Loaded:
    case LogisticStatus.OutForDelivery:
      return 'warning';
    case LogisticStatus.Delivered:
      return 'success';
    case LogisticStatus.PartiallyDelivered:
      return 'secondary';
    case LogisticStatus.CouldNotBeDelivered:
    case LogisticStatus.Cancelled:
      return 'error';
    default:
      return 'default';
  }
};

const StatusTag = ({ status }: { status: LogisticStatus }) => (
  <Chip label={LogisticStatusLabels[status]} size="small" />
);

// New component for Actions cell
function ActionsCell({ row }: { row: BuyerOrderModel }) {
  const actionsPopover = usePopover();
  const { mutate: cancelOrder } = useCancelOrder();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: cart } = useGetCart();

  const { mutate: addMultipleProductsToCart, isLoading: isAddMultipleProductsToCart } =
    useAddMultipleToCart();

  const productsInCart = useMemo(() => cart?.baskets?.flatMap((basket) => basket.items), [cart]);

  const anyProductsInCart = useMemo(
    () =>
      productsInCart?.some((item) =>
        row.products.some((product: BuyerOrderProductModel) => product.productName === item.name)
      ),
    [row.products, productsInCart]
  );

  const handleAddOrderToCart = useCallback(() => {
    actionsPopover.onClose();
    addMultipleProductsToCart(
      {
        body: row.products.map((product: BuyerOrderProductModel) => ({
          productStockId: product.productStockId,
          quantity: product.requestedQuantity,
        })),
      },
      {
        onSuccess: (response) => {
          enqueueSnackbar('Successfully added to basket');
          queryClient.refetchQueries(['orders']);
          queryClient.setQueryData(['cart'], response);
        },
      }
    );
  }, [actionsPopover, addMultipleProductsToCart, enqueueSnackbar, queryClient, row.products]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCancelOrder = useCallback(() => {
    cancelOrder(
      {
        pathParams: { orderId: row.id },
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Order Cancelled');
          queryClient.refetchQueries(['orders']);
        },
      }
    );
  }, [cancelOrder, enqueueSnackbar, queryClient, row.id]);

  return (
    <Box>
      <Tooltip
        title={
          anyProductsInCart
            ? 'Some products are already in your basket.'
            : 'Add entire order to basket.'
        }
        arrow
      >
        <span>
          <IconButton
            disabled={anyProductsInCart || isAddMultipleProductsToCart}
            onClick={handleAddOrderToCart}
            aria-label="Add order to basket"
          >
            <Iconify icon="solar:cart-3-bold" />
          </IconButton>
        </span>
      </Tooltip>
      <Button
        size="small"
        variant="outlined"
        onClick={() => navigate({ to: paths.dashboard.orders.orderDetails(row.id) })}
      >
        Order Details
      </Button>
    </Box>
  );
}

export default function OrderHistoryTable({
  orders = [],
  ordersTotalCount = 0,
  isLoading = false,
  paginationModel,
  ordersTotalPages,
  sortModel,
  // filterModel,
  // onFilterChanged,
  onSortChanged,
  onPaginationModelChanged,
}: Props) {
  const columns = useMemo<GridColDef<BuyerOrderModel>[]>(
    () => [
      // Order status is currently hidden for now until we have better on which to base them
      // {
      //   field: 'logisticStatus',
      //   headerName: 'Status',
      //   width: 130,
      //   renderCell: ({ row }: { row: any }) => <StatusTag status={row.logisticStatus} />,
      // },
      {
        field: 'hasIssue',
        headerName: 'Issue',
        sortable: false,
        width: 56,
        renderCell: ({ row }) => {
          if (row.hasIssue || row.products.some((prod) => prod.hasIssue)) {
            return (
              <Tooltip title="You have logged an issue with this order" arrow placement="top">
                <Box alignItems="center" display="flex">
                  <Iconify icon="eva:alert-triangle-fill" width={24} color="orange" />
                </Box>
              </Tooltip>
            );
          }
          return null;
        },
        renderHeader: () => null,
      },
      {
        field: 'orderReferenceNumber',
        headerName: 'PO Number',
        flex: 1,
        renderCell: ({ row }) => (
          <Stack direction="row" alignItems="center">
            <Box>{row.orderReferenceNumber}</Box>
            <CopyToClipboard copiedText={row.orderReferenceNumber} />
          </Stack>
        ),
      },
      {
        field: 'orderReference',
        headerName: 'Order Reference',
        sortable: false,
        width: 140,
        renderCell: ({ row }) => row.orderReference || 'N/A',
      },
      {
        field: 'createdOn',
        headerName: 'Ordered On',
        width: 120,
        renderCell: ({ row }) => {
          const date = new Date(row.createdOn);
          return date.toLocaleString(undefined, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
          });
        },
      },
      {
        field: 'orderedBy',
        headerName: 'Ordered By',
        width: 200,
        renderCell: ({ row }) => row.orderedByUserName || 'N/A',
      },
      {
        field: 'distributorName',
        headerName: 'Supplier',
        width: 140,
        renderCell: ({ row }) => row.distributorName || 'None',
      },
      {
        field: 'deliveryDate',
        headerName: 'Delivery Date',
        width: 120,
        renderCell: ({ row }) => {
          if (!row.deliveryDate) {
            return 'N/A';
          }
          const date = new Date(row.deliveryDate);
          return date.toLocaleString(undefined, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
          });
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 200,
        sortable: false,
        renderCell: ({ row }) => <ActionsCell row={row} />,
      },
    ],
    [] // Dependencies for columns are now handled within ActionsCell
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const mobileComponent = useMemo(
    () => (
      <Box sx={{ p: 0 }}>
        {orders.map((order) => (
          <Card sx={{ mb: 2 }} key={order.id}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <StatusTag status={order.logisticStatus} />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(order.createdOn).toLocaleDateString()}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1">PO: {order.orderReferenceNumber}</Typography>
                    <CopyToClipboard copiedText={order.orderReferenceNumber} />
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Order Ref: {order.orderReference || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      By: {order.orderedByUserEmail || 'N/A'}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Supplier: {order.distributorName || 'None'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Delivery:{' '}
                      {order.deliveryDate
                        ? new Date(order.deliveryDate).toLocaleDateString()
                        : 'N/A'}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}
        {paginationModel && (
          <Pagination
            count={ordersTotalPages}
            page={paginationModel.page + 1}
            onChange={(_event, page) => {
              if (onPaginationModelChanged) {
                const newPaginationModel = { ...paginationModel, page: page - 1 };
                onPaginationModelChanged(newPaginationModel);
              }
            }}
          />
        )}
      </Box>
    ),
    [onPaginationModelChanged, paginationModel, orders, ordersTotalPages]
  );

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    // Only keep the last sort model item if multiple are provided
    const singleFieldSortModel = newSortModel.slice(-1);
    onSortChanged(singleFieldSortModel);
  };

  return (
    <Box>
      <LoadingBarHorizontal showLoading={false} />
      {isMobile ? (
        mobileComponent
      ) : (
        <FillHeight>
          <DataGrid
            rows={orders}
            columns={columns}
            loading={isLoading}
            rowCount={ordersTotalCount}
            paginationMode="server"
            sortingMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChanged}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            getRowId={(row) => row.id}
            disableColumnMenu
            disableRowSelectionOnClick
            sx={{
              height: '100%',
              width: '100%',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'background.paper',
              },
              '& .MuiDataGrid-cell': {
                padding: '12px 16px',
              },
            }}
            initialState={{
              sorting: { sortModel },
            }}
            pageSizeOptions={[]} // Hide the page size options
          />
        </FillHeight>
      )}
    </Box>
  );
}
